/**
 * Precast geometry services (BIM Precast tab).
 *
 * Pure geometry math shared by the Split / Reinforcement tools, the renderers
 * and the fabrication exporters:
 *  - Monolithic wall / floor splitting into transportable panels per the
 *    Configuration store rules (max width, max height, weight envelope,
 *    joint gaps, edge gaps, minimum width merge).
 *  - Convex half-plane polygon clipping (Sutherland–Hodgman) for floor strips.
 *  - Rule-based reinforcement generation (welded mesh / bar layers) with
 *    covers from the Configuration.
 *  - Weight and quantity estimators used by shop drawings / CAM.
 *
 * Units: geometryData is METERS (project convention); the Configuration store
 * speaks millimeters; diameters on bars stay millimeters like RebarElement.
 */
import { v4 as uuidv4 } from 'uuid';
import {
    BIMElement,
    Element,
    FloorElement,
    WallElement,
    PrecastWallElement,
    PrecastFloorElement,
    PrecastColumnElement,
    PrecastBeamElement,
    PrecastReinforcementElement,
    PrecastBarSegment,
    PRECAST_HOST_CATEGORIES,
} from '../core/types';
import { getPrecastConfig, PrecastConfiguration } from '../state/usePrecastConfigStore';

const M_PER_MM = 0.001;
/** Hard guard: never generate more than N panels per element in one click. */
const MAX_PANELS = 200;
/** Hard guard: never generate more than N bars per reinforcement element. */
const MAX_BARS = 600;

// ==================== Polygon helpers ====================

export type Poly2D = [number, number][];

/** Signed-area-based orientation check (CCW positive). */
export function polygonArea(boundary: Poly2D): number {
    let a = 0;
    for (let i = 0; i < boundary.length; i++) {
        const [x1, y1] = boundary[i];
        const [x2, y2] = boundary[(i + 1) % boundary.length];
        a += x1 * y2 - x2 * y1;
    }
    return a / 2;
}

/** Axis-aligned bounding box of a polygon. */
export function polygonBounds(boundary: Poly2D): { minX: number; minY: number; maxX: number; maxY: number } {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of boundary) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
    }
    return { minX, minY, maxX, maxY };
}

/**
 * Clip a polygon to the half-plane { p | dot(n, p) <= c } (Sutherland–Hodgman).
 * Returns the clipped loop (possibly empty). n must be a unit vector.
 */
export function clipPolygonHalfPlane(poly: Poly2D, nx: number, ny: number, c: number): Poly2D {
    const out: Poly2D = [];
    const inside = (p: Poly2D[number]) => p[0] * nx + p[1] * ny <= c + 1e-9;
    for (let i = 0; i < poly.length; i++) {
        const cur = poly[i];
        const nxt = poly[(i + 1) % poly.length];
        const curIn = inside(cur);
        const nxtIn = inside(nxt);
        if (curIn) out.push(cur);
        if (curIn !== nxtIn) {
            const dc = cur[0] * nx + cur[1] * ny - c;
            const dn = nxt[0] * nx + nxt[1] * ny - c;
            const t = dc / (dc - dn);
            out.push([cur[0] + t * (nxt[0] - cur[0]), cur[1] + t * (nxt[1] - cur[1])]);
        }
    }
    return out.length >= 3 ? out : [];
}

/**
 * Clip a polygon to the axis-aligned (in rotated frame) strip
 * uMin <= u <= uMax where u = x·cos(a) + y·sin(a).
 */
export function clipPolygonStrip(poly: Poly2D, angle: number, uMin: number, uMax: number): Poly2D {
    const cu = Math.cos(angle), su = Math.sin(angle);
    // u axis: (cu, su); clip dot(u,p) <= uMax and -dot(u,p) <= -uMin
    let out = clipPolygonHalfPlane(poly, cu, su, uMax);
    if (out.length === 0) return out;
    out = clipPolygonHalfPlane(out, -cu, -su, -uMin);
    return out;
}

/** Ensure CCW orientation for extrusion-facing consumers. */
export function ensureCCW(poly: Poly2D): Poly2D {
    return polygonArea(poly) < 0 ? [...poly].reverse() : poly;
}

// ==================== Split planning ====================

export interface SplitStrip {
    /** Index of the panel (1-based for marks). */
    index: number;
    /** Strip extents along the cut axis in meters (u = world·axis). */
    uMin: number;
    uMax: number;
}

/**
 * Plan equal panel strips over a total run so each strip is at most
 * maxSpan (m) wide and leaves `gap` (m) joints between neighbors.
 * `minSpan` slivers are avoided by re-distributing with fewer strips.
 */
export function planStrips(runMeters: number, maxSpan: number, gap: number, minSpan: number): SplitStrip[] {
    if (runMeters <= 0 || maxSpan <= 0) return [];
    // Smallest n whose equal distribution respects the max width.
    let n = Math.max(1, Math.ceil((runMeters + gap) / (maxSpan + gap)));
    n = Math.min(n, MAX_PANELS);
    let width = (runMeters - (n - 1) * gap) / n;
    // A strip narrower than minSpan is pointless: drop a cut instead.
    while (n > 1 && width < minSpan) {
        n -= 1;
        width = (runMeters - (n - 1) * gap) / n;
    }
    if (width <= 0) return [];
    const strips: SplitStrip[] = [];
    let u = 0;
    for (let i = 0; i < n; i++) {
        strips.push({ index: i + 1, uMin: u, uMax: u + width });
        u += width + gap;
    }
    return strips;
}

/** Wall panel weight estimate, kg (density from Configuration). */
export function wallPanelWeightMeters(panel: PrecastWallElement, densityKgM3: number): number {
    const g = panel.geometryData;
    const length = Math.hypot(g.end[0] - g.start[0], g.end[1] - g.start[1]);
    return length * g.thickness * g.height * densityKgM3;
}

/** Floor panel weight estimate, kg; hollow-core voids deducted. */
export function floorPanelWeightMeters(panel: PrecastFloorElement, densityKgM3: number): number {
    const g = panel.geometryData;
    const area = Math.abs(polygonArea(g.boundary));
    let volume = area * g.thickness;
    if (g.hollowCore && g.core) {
        const spanLen = panelSpanLength(panel);
        const voidArea = g.core.count * (Math.PI / 4) * g.core.diameter * g.core.diameter;
        volume = Math.max(volume - voidArea * spanLen, 0.05 * area * g.thickness);
    }
    return volume * densityKgM3;
}

/** Panel length along the span axis (bounding diagonal of the footprint). */
export function panelSpanLength(panel: PrecastFloorElement): number {
    const g = panel.geometryData;
    const b = polygonBounds(g.boundary);
    const cu = Math.cos(g.spanAngle), su = Math.sin(g.spanAngle);
    const corners: Poly2D = [
        [b.minX, b.minY], [b.maxX, b.minY], [b.maxX, b.maxY], [b.minX, b.maxY],
    ];
    let minU = Infinity, maxU = -Infinity;
    for (const [x, y] of corners) {
        const u = x * cu + y * su;
        if (u < minU) minU = u;
        if (u > maxU) maxU = u;
    }
    return maxU - minU;
}

// ==================== Element construction ====================

/** Shared identity/parameter scaffold for every generated precast element.
 *  `category` is supplied explicitly at each call site so the literal
 *  category narrowing of the element interfaces is preserved. */
function precastBase(source: Element, type: string, mark: string, parentElementId?: string) {
    return {
        id: uuidv4(),
        name: mark,
        type,
        parameters: {
            ...(source.parameters || {}),
            'Mark': mark,
            'Panel Type': type,
            'Manufacturing Status': 'To Fabricate',
            ...(parentElementId ? { 'Source Element Id': parentElementId } : {}),
        },
        constraints: [],
        phaseCreatedId: source.phaseCreatedId,
        designOptionId: source.designOptionId,
    };
}

/**
 * Split a monolithic (cast-in-place) wall into precast wall panels per the
 * active Configuration. The wall run is consumed between edge gaps, joints
 * are reserved between panels; panel count is raised until the weight and
 * max-height envelopes hold.
 */
export function splitWallToPanels(wall: WallElement, config: PrecastConfiguration = getPrecastConfig()): PrecastWallElement[] {
    const g = wall.geometryData;
    const start = { x: g.start[0], y: g.start[1], z: g.start[2] };
    const end = { x: g.end[0], y: g.end[1], z: g.end[2] };
    const lengthM = Math.hypot(end.x - start.x, end.y - start.y);
    if (lengthM <= 0.05) return [];

    const edgeGap = Math.max(config.edgeGapMm, 0) * M_PER_MM;
    const gap = Math.max(config.jointGapMm, 0) * M_PER_MM;
    const height = g.height ?? 3;
    const thickness = g.thickness ?? 0.2;

    // Max width allowed by the weight envelope: W <= maxKg / (t*h*density)
    const density = Math.max(config.concreteDensityKgM3, 1000);
    const maxWidthByWeight = (config.maxPanelWeightKg / (thickness * Math.min(height, config.maxPanelHeightMm * M_PER_MM) * density));
    const maxSpan = Math.max(Math.min(config.maxPanelWidthMm * M_PER_MM, maxWidthByWeight), 0.4);

    const run = Math.max(lengthM - 2 * edgeGap, 0.1);
    const strips = planStrips(run, maxSpan, gap, config.minPanelWidthMm * M_PER_MM);
    if (strips.length === 0) return [];

    const ux = (end.x - start.x) / lengthM;
    const uy = (end.y - start.y) / lengthM;

    return strips.map((strip) => {
        const s = edgeGap + strip.uMin;
        const e = edgeGap + strip.uMax;
        const mark = `WP-${strip.index}`;
        const panel: PrecastWallElement = {
            ...precastBase(wall, 'Precast Wall Panel', mark, wall.id),
            category: 'Precast Walls',
            geometryData: {
                start: [start.x + ux * s, start.y + uy * s, start.z],
                end: [start.x + ux * e, start.y + uy * e, start.z],
                height: Math.min(height, config.maxPanelHeightMm * M_PER_MM),
                thickness,
                baseOffset: g.baseOffset ?? 0,
                jointWidth: gap,
                parentElementId: wall.id,
                panelIndex: strip.index,
                panelCount: strips.length,
                mark,
            },
        };
        return panel;
    });
}

/**
 * Split a monolithic floor slab into precast panels. The cut axis is the
 * span direction (long bbox side by default, or geometryData.spanDirection).
 * Hollow-core mode slices standard plank widths; solid mode uses the max
 * panel width envelope. Joints are reserved between strips.
 */
export function splitFloorToPanels(floor: FloorElement, config: PrecastConfiguration = getPrecastConfig()): PrecastFloorElement[] {
    const g = floor.geometryData;
    if (!g?.boundary || g.boundary.length < 3) return [];
    const boundary: Poly2D = g.boundary.map(p => [p[0], p[1]] as [number, number]);
    const b = polygonBounds(boundary);
    const thickness = g.thickness ?? 0.2;
    const levelOffset = g.heightOffsetFromLevel ?? 0;

    // Span axis: explicit spanDirection wins, else the longer bbox side.
    let spanAngle = (b.maxX - b.minX) >= (b.maxY - b.minY) ? 0 : Math.PI / 2;
    if (Array.isArray(g.spanDirection)) {
        const [dx, dy] = g.spanDirection as [number, number, number];
        if (Math.abs(dx) + Math.abs(dy) > 1e-6) spanAngle = Math.atan2(dy, dx);
    }
    const cu = Math.cos(spanAngle), su = Math.sin(spanAngle);
    let uMin = Infinity, uMax = -Infinity;
    for (const [x, y] of boundary) {
        const u = x * cu + y * su;
        if (u < uMin) uMin = u;
        if (u > uMax) uMax = u;
    }
    const run = uMax - uMin;
    if (run <= 0.05) return [];

    const gap = Math.max(config.jointGapMm, 5) * M_PER_MM; // planks keep ≥5 mm bed gap
    const density = Math.max(config.concreteDensityKgM3, 1000);
    let maxWidth = (config.floorMode === 'HollowCore'
        ? config.hollowCoreWidthMm
        : config.maxPanelWidthMm) * M_PER_MM;
    // Solid slabs also respect the weight envelope (full plane stress estimate).
    if (config.floorMode !== 'HollowCore') {
        maxWidth = Math.min(maxWidth, config.maxPanelWeightKg / (run * thickness * density * 0.25 + 1e-6));
        maxWidth = Math.max(Math.min(maxWidth, config.maxPanelWidthMm * M_PER_MM), 0.4);
    }

    const strips = planStrips(run, maxWidth, gap, config.floorMode === 'HollowCore' ? 0.3 : config.minPanelWidthMm * M_PER_MM);
    if (strips.length === 0) return [];

    const prefix = config.floorMode === 'HollowCore' ? 'HP' : 'SP';
    const typeName = config.floorMode === 'HollowCore' ? 'Precast Hollow Core Slab' : 'Precast Solid Slab';
    const coreDiameter = 0.1; // 100 mm void, standard HC product family

    return strips.map((strip) => {
        // planStrips already reserves the `gap` joint between neighbors, so
        // the clip uses the exact strip bounds — insetting again would double
        // the joint.
        const clipped = ensureCCW(clipPolygonStrip(boundary, spanAngle, uMin + strip.uMin, uMin + strip.uMax));
        if (clipped.length < 3 || Math.abs(polygonArea(clipped)) < 0.02) return null;
        const mark = `${prefix}-${strip.index}`;
        // planStrips sized each strip to the final panel width already.
        const widthM = strip.uMax - strip.uMin;
        const coreCount = config.floorMode === 'HollowCore'
            ? Math.max(1, Math.floor(widthM / (coreDiameter * 2)))
            : 0;
        const panel: PrecastFloorElement = {
            ...precastBase(floor, typeName, mark, floor.id),
            category: 'Precast Floors',
            geometryData: {
                boundary: clipped,
                thickness,
                baseZ: levelOffset - thickness,
                spanAngle,
                hollowCore: config.floorMode === 'HollowCore',
                core: coreCount > 0
                    ? { diameter: coreDiameter, spacing: widthM / coreCount, count: coreCount }
                    : undefined,
                parentElementId: floor.id,
                panelIndex: strip.index,
                panelCount: strips.length,
                mark,
                jointWidth: gap,
            },
        };
        return panel;
    }).filter((p): p is PrecastFloorElement => p !== null);
}

/** Build a standalone precast column from a base point. */
export function createPrecastColumn(
    base: [number, number, number],
    options: { widthMm: number; depthMm: number; heightMm: number; mark: string; source?: Element },
): PrecastColumnElement {
    const w = options.widthMm * M_PER_MM;
    const d = options.depthMm * M_PER_MM;
    const h = options.heightMm * M_PER_MM;
    const src: Element = options.source ?? ({ id: uuidv4(), category: 'Precast Columns', type: '', parameters: {}, constraints: [] } as Element);
    return {
        ...precastBase(src, 'Precast Column', options.mark, options.source?.id),
        category: 'Precast Columns',
        geometryData: {
            start: [base[0], base[1], base[2]],
            end: [base[0], base[1], base[2] + h],
            width: Math.max(w, 0.15),
            depth: Math.max(d, 0.15),
            jointWidth: 0,
            panelIndex: 0,
            panelCount: 0,
            mark: options.mark,
        },
    };
}

/** Build a standalone precast beam from two points. */
export function createPrecastBeam(
    start: [number, number, number],
    end: [number, number, number],
    options: { widthMm: number; depthMm: number; mark: string; source?: Element },
): PrecastBeamElement {
    const src: Element = options.source ?? ({ id: uuidv4(), category: 'Precast Beams', type: '', parameters: {}, constraints: [] } as Element);
    return {
        ...precastBase(src, 'Precast Beam', options.mark, options.source?.id),
        category: 'Precast Beams',
        geometryData: {
            start,
            end,
            width: Math.max(options.widthMm * M_PER_MM, 0.1),
            depth: Math.max(options.depthMm * M_PER_MM, 0.15),
            jointWidth: 0,
            panelIndex: 0,
            panelCount: 0,
            mark: options.mark,
        },
    };
}

// ==================== Reinforcement generation ====================

/**
 * Generate the rule-based reinforcement mesh for a precast member. Mirrors
 * BIM's automatic precast reinforcement: covers from Configuration, bar
 * diameter/spacing from the tool (falling back to config), hollow-core decks
 * get a single top mesh, solid members get top+bottom layers, wall panels get
 * a two-face vertical/horizontal mesh.
 */
export function generatePrecastReinforcement(
    host: BIMElement,
    options: { wireDiameterMm?: number; spacingMm?: number } = {},
    config: PrecastConfiguration = getPrecastConfig(),
): PrecastReinforcementElement | null {
    const diameter = Math.max(options.wireDiameterMm ?? config.rebarDiameterMm, 3);
    const spacing = Math.max(options.spacingMm ?? config.rebarSpacingMm, 50);
    const cover = Math.max(config.coverMm, 10) * M_PER_MM;
    const s = spacing * M_PER_MM;
    const bars: PrecastBarSegment[] = [];

    const push = (
        start: [number, number, number],
        end: [number, number, number],
        role: PrecastBarSegment['role'],
    ) => {
        if (bars.length < MAX_BARS) bars.push({ start, end, diameter, role });
    };

    if (host.category === 'Precast Walls') {
        const g = (host as PrecastWallElement).geometryData;
        const length = Math.hypot(g.end[0] - g.start[0], g.end[1] - g.start[1]);
        if (length < 0.2 || g.height < 0.2) return null;
        const ux = (g.end[0] - g.start[0]) / length;
        const uy = (g.end[1] - g.start[1]) / length;
        const twoFace = g.thickness > 0.1 && config.reinforcementStyle !== 'Single Layer Bars';
        const faces = twoFace
            ? [-(g.thickness / 2 - cover), g.thickness / 2 - cover]
            : [0];
        for (const face of faces) {
            const offset = (u: number, z: number): [number, number, number] => [
                g.start[0] + ux * u - uy * face,
                g.start[1] + uy * u + ux * face,
                g.baseOffset + z,
            ];
            // Vertical bars along the panel length.
            const verticalCount = Math.floor((length - 2 * cover) / s) + 1;
            for (let i = 0; i < verticalCount; i++) {
                const u = cover + i * s;
                if (u > length - cover) break;
                push(offset(u, cover), offset(u, g.height - cover), 'vertical');
            }
            // Horizontal bars up the panel height.
            const horizontalCount = Math.floor((g.height - 2 * cover) / s) + 1;
            for (let i = 0; i < horizontalCount; i++) {
                const z = cover + i * s;
                if (z > g.height - cover) break;
                push(offset(cover, z), offset(length - cover, z), 'horizontal');
            }
        }
    } else if (host.category === 'Precast Floors') {
        const g = (host as PrecastFloorElement).geometryData;
        const transverse = g.spanAngle + Math.PI / 2;
        const cu = Math.cos(g.spanAngle), su = Math.sin(g.spanAngle);
        const tu = Math.cos(transverse), tv = Math.sin(transverse);
        // Panel strips are axis-aligned in the (u, v) span frame, so the
        // rotated-frame extents of the boundary are the exact bar limits.
        let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
        for (const [x, y] of g.boundary) {
            const u = x * cu + y * su;
            const v = x * tu + y * tv;
            if (u < uMin) uMin = u;
            if (u > uMax) uMax = u;
            if (v < vMin) vMin = v;
            if (v > vMax) vMax = v;
        }
        if (uMax - uMin < 0.1 || vMax - vMin < 0.1) return null;
        const layers: number[] = g.hollowCore || config.reinforcementStyle === 'Single Layer Bars'
            ? [g.baseZ + g.thickness - cover]
            : [g.baseZ + g.thickness - cover, g.baseZ + cover];
        const toWorld = (u: number, v: number, z: number): [number, number, number] =>
            [u * cu + v * tu, u * su + v * tv, z];
        for (const z of layers) {
            // Main bars along the span, one per transverse station.
            const vCount = Math.floor((vMax - vMin - 2 * cover) / s) + 1;
            for (let i = 0; i < vCount; i++) {
                const v = vMin + cover + i * s;
                if (v > vMax - cover) break;
                push(toWorld(uMin + cover, v, z), toWorld(uMax - cover, v, z), 'longitudinal');
            }
            // Distribution bars transverse, one per span station.
            const uCount = Math.floor((uMax - uMin - 2 * cover) / s) + 1;
            for (let i = 0; i < uCount; i++) {
                const u = uMin + cover + i * s;
                if (u > uMax - cover) break;
                push(toWorld(u, vMin + cover, z), toWorld(u, vMax - cover, z), 'transverse');
            }
        }
    } else if (host.category === 'Precast Columns' || host.category === 'Precast Beams') {
        const g = (host as PrecastColumnElement | PrecastBeamElement).geometryData;
        const length = Math.hypot(
            g.end[0] - g.start[0], g.end[1] - g.start[1], g.end[2] - g.start[2],
        );
        if (length < 0.2) return null;
        const dir: [number, number, number] = [
            (g.end[0] - g.start[0]) / length,
            (g.end[1] - g.start[1]) / length,
            (g.end[2] - g.start[2]) / length,
        ];
        // Local frame perpendicular to the axis.
        const up: [number, number, number] = Math.abs(dir[2]) > 0.9 ? [1, 0, 0] : [0, 0, 1];
        const ax = dir[1] * up[2] - dir[2] * up[1];
        const ay = dir[2] * up[0] - dir[0] * up[2];
        const az = dir[0] * up[1] - dir[1] * up[0];
        const aLen = Math.hypot(ax, ay, az) || 1;
        const px = ax / aLen, py = ay / aLen, pz = az / aLen;
        const qx = dir[1] * pz - dir[2] * py;
        const qy = dir[2] * px - dir[0] * pz;
        const qz = dir[0] * py - dir[1] * px;
        const halfW = Math.max(g.width / 2 - cover, 0.01);
        const halfD = Math.max(g.depth / 2 - cover, 0.01);
        // 4 corner longitudinal bars (section frame: p = width axis, q = depth).
        for (const [sw, sd] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
            const ox = g.start[0] + px * sw * halfW + qx * sd * halfD;
            const oy = g.start[1] + py * sw * halfW + qy * sd * halfD;
            const oz = g.start[2] + pz * sw * halfW + qz * sd * halfD;
            push([ox, oy, oz], [ox + dir[0] * length, oy + dir[1] * length, oz + dir[2] * length], 'longitudinal');
        }
        // Transverse links / stirrups.
        const linkCount = Math.floor((length - 2 * cover) / s) + 1;
        for (let i = 0; i < linkCount; i++) {
            const t = cover + i * s;
            if (t > length - cover) break;
            const cx = g.start[0] + dir[0] * t;
            const cy = g.start[1] + dir[1] * t;
            const cz = g.start[2] + dir[2] * t;
            push([cx - px * halfW, cy - py * halfW, cz - pz * halfW], [cx + px * halfW, cy + py * halfW, cz + pz * halfW], 'transverse');
            push([cx - qx * halfW, cy - qy * halfW, cz - qz * halfW], [cx + qx * halfW, cy + qy * halfW, cz + qz * halfW], 'transverse');
        }
    } else {
        return null;
    }

    if (bars.length === 0) return null;

    return {
        ...precastBase(host as Element, config.reinforcementStyle, `Mesh-${host.name || host.id.slice(0, 4)}`),
        category: 'Precast Reinforcement',
        geometryData: {
            hostId: host.id,
            hostCategory: host.category,
            coverTop: cover,
            coverBottom: cover,
            coverSides: cover,
            wireDiameter: diameter,
            spacing: spacing,
            twoFace: config.reinforcementStyle !== 'Single Layer Bars' && !((host as any).geometryData?.hollowCore),
            bars,
        },
    };
}

/** Total bar length (meters) of a reinforcement element. */
export function reinforcementTotalLength(reinf: PrecastReinforcementElement): number {
    return reinf.geometryData.bars.reduce(
        (sum, b) => sum + Math.hypot(b.end[0] - b.start[0], b.end[1] - b.start[1], b.end[2] - b.start[2]),
        0,
    );
}

/** Reinforcement weight, kg (steel: d²mm × 0.00617 per meter). */
export function reinforcementWeightKg(reinf: PrecastReinforcementElement): number {
    return reinforcementTotalLength(reinf) * reinf.geometryData.wireDiameter * reinf.geometryData.wireDiameter * 0.00617;
}

/** True when the element is a monolithic host the Split tool accepts. */
export function isSplitSource(element: BIMElement): boolean {
    return element.category === 'Walls' || element.category === 'Floors';
}

/** True when the element can host precast mounting parts / reinforcement. */
export function isPrecastHost(element: BIMElement): boolean {
    return (PRECAST_HOST_CATEGORIES as readonly string[]).includes(element.category);
}
